import { trimStart } from 'lodash';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getInternalFolders } from '~/api/evisync';
import { showToast } from '~/components/Shared/EcToast/showToast';
import ProviderLogo from '~/components/Shared/ProviderLogo/ProviderLogo';
import { DateInput } from '~/eds/src/components/DateInput/DateInput';
import { Divider } from '~/eds/src/components/Divider/Divider';
import { FileInput } from '~/eds/src/components/FileInput/FileInput';
import { Files } from '~/eds/src/components/Files/Files';
import { FormField } from '~/eds/src/components/FormField/FormField';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Modal } from '~/eds/src/components/Modal/Modal';
import { Select } from '~/eds/src/components/Select/Select';
import { Text } from '~/eds/src/components/Text/Text';
import { HtmlEntityType } from '~/eds/src/enums/HtmlEntityType';
import { AribaIntegrationType } from '~/enums/AribaIntegrationType';
import { FileExtensionType } from '~/enums/FileExtensionType';
import { FileMimeType } from '~/enums/FileMimeType';
import { ManageSyncPairStageType } from '~/enums/ManageSyncPairStageType';
import { useAsync } from '~/hooks/useAsync';
import { FormattedMessage, useIntl } from 'react-intl';
import { api } from '~/redux/api/createApi';
import { slices } from '~/redux/slices';
import { ERROR } from '~/types/toast.types';
import FolderInput from '~/ui/components/FolderInput/FolderInput';

import {
  DEFAULT_FILE_TYPES,
  PROCESSABLE_DOCUMENT_TYPES,
} from '../../constants';
import AribaFooter from '../AribaFooter';
import AribaMappingSetup from './AribaMappingSetup';

function FolderAriba() {
  const intl = useIntl();
  const dispatch = useDispatch();

  const activeSyncPair = useSelector(
    slices.integrations.selectors.selectActiveSyncPair,
  );
  const manageSyncPairStage = useSelector(
    slices.integrations.selectors.selectManageSyncPairStage,
  );
  const { provider, allowedSyncFileTypes, syncPair, syncType } = activeSyncPair;
  const stageProperties = activeSyncPair[manageSyncPairStage] ?? {};
  const { evisortFolder } = stageProperties;
  const docIdsFile = stageProperties['docIdsFile'] ?? [];
  const [sinceDate, setSinceDate] = useState(null);
  const [count, setCount] = useState(null);
  const [localDocFile, setLocalDocFile] = useState(null);

  const [getAribaCounts] = api.endpoints.getAribaWorkspaceCount.useLazyQuery();
  const [
    isValidationAribaDocumentIdFileVisible,
    setIsValidationAribaDocumentIdFileVisible,
  ] = React.useState(false);

  const [validateAribaDocumentIdFile] =
    api.endpoints.validateAribaDocumentIdFile.useMutation();

  const getAribaCount = async (selectedDate) => {
    const updatedValues = {
      ...stageProperties,
      sinceDate: selectedDate,
    };
    setSinceDate(selectedDate);
    try {
      const response = await getAribaCounts({
        sinceDate: selectedDate,
        syncPair,
      });
      const cwCount = response.data.workspaces?.totalRecords;
      setCount(cwCount);
      updatedValues.cwCount = cwCount;
    } catch {
      showToast(
        ERROR,
        intl.formatMessage({
          id: 'FolderAriba.aribaCountError',
          defaultMessage:
            'Something went wrong getting the number of Ariba workspaces',
          description:
            'Error message when fetching Ariba workspace count fails',
        }),
      );
    } finally {
      dispatch(
        slices.integrations.actions.setActiveSyncPair({
          ...activeSyncPair,
          [manageSyncPairStage]: updatedValues,
        }),
      );
    }
  };

  const { isLoading: isLoadingFolders, response: folders = [] } = useAsync(
    getInternalFolders,
    {},
    {
      condition: true,
      errorToastMessage: intl.formatMessage({
        id: 'FolderAriba.foldersError',
        defaultMessage:
          'There is an error fetching Evisort folders, please try refreshing the page.',
        description: 'Error message when fetching Evisort folders fails',
      }),
    },
  );

  const handleBack = () => {
    dispatch(
      slices.integrations.actions.setManageSyncPairStage(
        ManageSyncPairStageType.FileConfig,
      ),
    );
  };

  const handleCreateNext = () => {
    dispatch(
      slices.integrations.actions.setManageSyncPairStage(
        ManageSyncPairStageType.FieldMapping,
      ),
    );
  };

  const setFolder = (evisortFolder) => {
    const updatedValues = {
      ...stageProperties,
      evisortFolder,
    };
    dispatch(
      slices.integrations.actions.setActiveSyncPair({
        ...activeSyncPair,
        [manageSyncPairStage]: updatedValues,
      }),
    );
  };

  const setSyncPairDocFile = (docIdsFile) => {
    dispatch(
      slices.integrations.actions.setActiveSyncPair({
        ...activeSyncPair,
        [manageSyncPairStage]: {
          ...stageProperties,
          docIdsFile,
        },
      }),
    );
  };

  const onFileChangeHandler = (docIdsFile) => {
    if (!docIdsFile) {
      return;
    }

    setLocalDocFile(docIdsFile);

    validateAribaDocumentIdFile({ file: docIdsFile[0] })
      .unwrap()
      .then(() => {
        // If the file is valid, update the activeSyncPair with the new file
        setSyncPairDocFile(docIdsFile);
      })
      .catch((error) => {
        const message = error.response?.data?.error ?? '';
        if (message.toLowerCase().includes('primary document')) {
          setIsValidationAribaDocumentIdFileVisible(true);
        }
      });
  };

  const selectedFileRendered = docIdsFile.length > 0 && (
    <Files
      title={intl.formatMessage({
        id: 'FolderAriba.uploadedFileTitle',
        defaultMessage: 'Uploaded file',
        description: 'Title for uploaded file section',
      })}
      files={[docIdsFile[0]]}
      onRemoveFile={() => setSyncPairDocFile([])}
    />
  );

  const fileTypeOptions = allowedSyncFileTypes.map((fileExtensionType) => {
    const option = {
      value: fileExtensionType,
      label: trimStart(fileExtensionType, '.'),
    };
    if (DEFAULT_FILE_TYPES.includes(fileExtensionType)) {
      option.isDefault = true;
    }
    return option;
  });

  const addOption = (value) => {
    return value;
  };

  const setAllowedSyncFileTypes = (types) => {
    dispatch(
      slices.integrations.actions.setActiveSyncPair({
        ...activeSyncPair,
        allowedSyncFileTypes: types.map((type) => '.' + trimStart(type, '.')),
      }),
    );
  };

  const handleWarningCancel = () => {
    setIsValidationAribaDocumentIdFileVisible(false);
    setLocalDocFile([]);
  };

  return syncType === AribaIntegrationType.CONTINUOUS ? (
    <AribaMappingSetup />
  ) : (
    <Layout align="center" direction="column" spacing={8}>
      <Layout align="center" py={2} spacing={6}>
        <ProviderLogo provider="evisort" />
        <Text color="text.quiet">{HtmlEntityType.DoubleDash}</Text>
        <ProviderLogo provider={provider} />
      </Layout>
      <Divider />
      <Layout direction="column" spacing={4} pb={4}>
        {syncType !== AribaIntegrationType.CONTINUOUS ? (
          <FormField
            required
            label={intl.formatMessage({
              id: 'FolderAriba.uploadDocumentIdsLabel',
              defaultMessage: 'Upload Ariba Document IDs to Sync',
              description: 'Label for uploading Ariba document IDs',
            })}
            description={intl.formatMessage({
              id: 'FolderAriba.uploadDocumentIdsDescription',
              defaultMessage:
                'Upload a .csv file with Ariba document IDs you would like to sync',
              description: 'Description for uploading Ariba document IDs',
            })}
            input={FileInput}
            value={docIdsFile}
            onChange={onFileChangeHandler}
            inputProps={{
              accept: {
                [FileMimeType.Csv]: [FileExtensionType.Csv],
              },
            }}
          />
        ) : (
          <FormField
            label={intl.formatMessage({
              id: 'FolderAriba.timeframeLabel',
              defaultMessage:
                'Add a timeframe to search for contract workspace',
              description: 'Label for timeframe selection',
            })}
            description={intl.formatMessage({
              id: 'FolderAriba.timeframeDescription',
              defaultMessage: 'Apply the modified date.',
              description: 'Description for timeframe selection',
            })}
            input={DateInput}
            statusMessage={
              count > 0
                ? {
                    message: intl.formatMessage(
                      {
                        id: 'FolderAriba.workspacesFoundMessage',
                        defaultMessage:
                          "We found <strong>{count}</strong> workspaces that can be synced based on the modified date. If a contract workspace doesn't contain any eligible documents, we will skip it to avoid creating an empty folder in Evisort.",
                        description:
                          'Message showing number of workspaces found. Example: count = 25',
                      },
                      { count },
                    ),
                  }
                : undefined
            }
            value={sinceDate}
            onChange={getAribaCount}
          />
        )}
        {selectedFileRendered}
        <FormField
          required
          label={intl.formatMessage({
            id: 'FolderAriba.selectFolderLabel',
            defaultMessage: 'Select an Evisort Folder',
            description: 'Label for Evisort folder selection',
          })}
          description={intl.formatMessage({
            id: 'FolderAriba.selectFolderDescription',
            defaultMessage: 'This is the destination folder',
            description: 'Description for Evisort folder selection',
          })}
          input={FolderInput}
          value={evisortFolder}
          onChange={setFolder}
          isLoading={isLoadingFolders}
          inputProps={{
            modalTitle: intl.formatMessage({
              id: 'FolderAriba.selectFolderModalTitle',
              defaultMessage: 'Select an Evisort folder',
              description: 'Modal title for selecting Evisort folder',
            }),
            folder: { ...evisortFolder },
            folderTreeProps: {
              label: intl.formatMessage({
                id: 'FolderAriba.selectPlaceholder',
                defaultMessage: 'Select...',
                description: 'Placeholder text for folder selection',
              }),
              folders: folders,
            },
          }}
        />
        <FormField
          required
          onChange={setAllowedSyncFileTypes}
          input={Select}
          description={
            <FormattedMessage
              id="FolderAriba.fileTypesDescription"
              defaultMessage="Selected file types will show in your Evisort folders, but only <strong>{processableTypes}</strong> will be processed by Evisort's AI."
              description="Description explaining file type processing. Example: processableTypes = 'document'"
              values={{
                strong: (chunks) => <strong>{chunks}</strong>,
                processableTypes: PROCESSABLE_DOCUMENT_TYPES.join(', '),
              }}
            />
          }
          label={intl.formatMessage({
            id: 'FolderAriba.fileTypesLabel',
            defaultMessage: 'Synchronize the following file types',
            description: 'Label for file types selection',
          })}
          value={allowedSyncFileTypes}
          inputProps={{
            enableErrorMessage: true,
            error:
              allowedSyncFileTypes.length === 0
                ? intl.formatMessage({
                    id: 'FolderAriba.fileTypesError',
                    defaultMessage:
                      'You have not added any file types. Please include at least one file type',
                    description:
                      'Error message when no file types are selected',
                  })
                : null,
            isMulti: true,
            noOptionsMessage: intl.formatMessage({
              id: 'FolderAriba.fileTypesNoOptions',
              defaultMessage:
                'Enter an extension for a file type you want to add',
              description: 'Message when no file type options available',
            }),
            placeholder: intl.formatMessage({
              id: 'FolderAriba.fileTypesPlaceholder',
              defaultMessage: 'Add a file extension',
              description: 'Placeholder for file types input',
            }),
            width: 'fullWidth',
            options: fileTypeOptions,
            onAddOption: addOption,
          }}
        />
      </Layout>
      <AribaFooter
        secondaryAction={{
          text: intl.formatMessage({
            id: 'FolderAriba.backButton',
            defaultMessage: 'Back',
            description: 'Button text for going back',
          }),
          onClick: handleBack,
        }}
        mainAction={{
          text: intl.formatMessage({
            id: 'FolderAriba.nextButton',
            defaultMessage: 'Next',
            description: 'Button text for proceeding to next step',
          }),
          onClick: handleCreateNext,
          isLoading: isLoadingFolders,
          disabled:
            !evisortFolder ||
            docIdsFile.length === 0 ||
            allowedSyncFileTypes.length === 0,
        }}
      />
      <Modal
        title={intl.formatMessage({
          id: 'FolderAriba.warningModalTitle',
          defaultMessage: 'Warning: Missing Primary Document Designation',
          description: 'Title for warning modal about missing primary document',
        })}
        cancelText={''}
        isVisible={isValidationAribaDocumentIdFileVisible}
        onHide={handleWarningCancel}
        primaryAction={{
          text: intl.formatMessage({
            id: 'FolderAriba.warningModalYes',
            defaultMessage: 'Yes',
            description: 'Yes button text in warning modal',
          }),
          onClick: () => {
            setIsValidationAribaDocumentIdFileVisible(false);
            setSyncPairDocFile(localDocFile);
          },
        }}
        secondaryActions={[
          {
            text: intl.formatMessage({
              id: 'FolderAriba.warningModalNo',
              defaultMessage: 'No',
              description: 'No button text in warning modal',
            }),
            variant: 'danger',
            onClick: handleWarningCancel,
          },
        ]}
      >
        <FormattedMessage
          id="FolderAriba.warningModalContent"
          defaultMessage="We did not find a Primary Document designation, which is necessary to send Evisort data back to Ariba. Continuing without a Primary Document designation will not impact your ability to sync files and data into Evisort.{br}{br} Do you want to continue?"
          description="Warning modal content about missing primary document designation"
          values={{
            br: <br />,
          }}
        />
      </Modal>
    </Layout>
  );
}

export default FolderAriba;
